// Admin Drive Upload Service
// Automatically uploads generated videos to admin's Google Drive

import { logger } from '../utils/logger';

export class AdminDriveService {
  private static instance: AdminDriveService;
  
  private constructor() {}
  
  static getInstance(): AdminDriveService {
    if (!AdminDriveService.instance) {
      AdminDriveService.instance = new AdminDriveService();
    }
    return AdminDriveService.instance;
  }

  /**
   * Upload video to admin's Google Drive folder via Google Apps Script
   * Uses Apps Script web app as a proxy to avoid googleapis dependency
   */
  async uploadVideoToAdminDrive(
    videoUrl: string,
    fileName: string,
    metadata?: {
      topic?: string;
      duration?: number;
      model?: string;
      segment?: number;
    }
  ): Promise<boolean> {
    try {
      // Check if Apps Script is configured
      const appsScriptUrl = import.meta.env.VITE_APPS_SCRIPT_URL;
      const appsScriptToken = import.meta.env.VITE_APPS_SCRIPT_TOKEN;
      
      if (!appsScriptUrl || !appsScriptToken) {
        logger.info('ADMIN_DRIVE_UPLOAD_SKIPPED', { 
          message: 'Apps Script not configured for admin Drive upload'
        });
        return false;
      }
      
      logger.info('ADMIN_DRIVE_UPLOAD_START', { fileName, metadata });
      
      // First, fetch the video and convert to base64
      const videoResponse = await fetch(videoUrl);
      const videoBlob = await videoResponse.blob();
      const base64 = await this.blobToBase64(videoBlob);
      
      // Call Apps Script web app
      const response = await fetch(appsScriptUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'text/plain',
        },
        body: JSON.stringify({
          token: appsScriptToken,
          base64,
          fileName,
          metadata: {
            ...metadata,
            userId: this.getUserIdentifier(),
            timestamp: new Date().toISOString()
          }
        }),
      });

      // Apps Script returns text/plain, parse it as JSON
      const responseText = await response.text();
      const data = JSON.parse(responseText);
      
      if (data.ok) {
        logger.info('ADMIN_DRIVE_UPLOAD_SUCCESS', { 
          fileId: data.fileId,
          fileName: data.fileName,
          fileUrl: data.fileUrl
        });
        return true;
      } else {
        logger.error('ADMIN_DRIVE_UPLOAD_ERROR', { 
          error: data.error
        });
        return false;
      }
    } catch (error) {
      logger.error('ADMIN_DRIVE_UPLOAD_EXCEPTION', { 
        error: error instanceof Error ? error.message : error 
      });
      return false;
    }
  }

  /**
   * Convert blob to base64 string
   */
  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Get a unique identifier for the current user
   */
  private getUserIdentifier(): string {
    // Try to get a consistent user identifier
    const stored = localStorage.getItem('storyshot-user-id');
    if (stored) {
      return stored;
    }
    
    // Generate a new one if not exists
    const newId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('storyshot-user-id', newId);
    return newId;
  }

  /**
   * Generate a sanitized filename for the video
   */
  generateFileName(topic: string, segment?: number, duration?: number): string {
    // Sanitize topic for filename
    const sanitized = topic
      .replace(/[^a-zA-Z0-9가-힣]/g, '_')
      .replace(/_+/g, '_')
      .substr(0, 50);
    
    const timestamp = new Date().toISOString().split('T')[0];
    const segmentPart = segment ? `_segment${segment}` : '';
    const durationPart = duration ? `_${duration}s` : '';
    
    return `StoryShot_${timestamp}_${sanitized}${segmentPart}${durationPart}.mp4`;
  }
}

// Export singleton instance
export const adminDriveService = AdminDriveService.getInstance();